import React, { useState, useEffect } from 'react';
import { getGlobalSettings, updateGlobalSettings, subscribeToSettings } from '../services/settingsService';

export default function DashboardContent({ currentUser }) {
  const [title, setTitle] = useState('');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // Real-time listener for settings
  useEffect(() => {
    // Fallback timeout in case Firestore hangs or is blocked by rules
    const timeoutId = setTimeout(() => {
      setLoading(false);
      setMessage({ text: 'Warning: Could not connect to database. Check Firestore rules.', type: 'error' });
    }, 5000);

    const unsubscribe = subscribeToSettings((data) => {
      clearTimeout(timeoutId);
      setTitle(data?.title || '');
      setMaintenanceMode(data?.maintenanceMode || false);
      setLoading(false);
    });

    return () => {
      clearTimeout(timeoutId);
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const handleSaveTitle = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ text: '', type: '' });
    
    const success = await updateGlobalSettings({ title });
    
    if (success) {
      setMessage({ text: 'Title saved successfully!', type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } else {
      setMessage({ text: 'Failed to save title. Please try again.', type: 'error' });
    }
    setSaving(false);
  };

  const handleToggleMaintenance = async () => {
    const newValue = !maintenanceMode;
    // Optimistic UI update
    setMaintenanceMode(newValue);
    
    const success = await updateGlobalSettings({ maintenanceMode: newValue });
    
    if (success) {
      setMessage({ text: `Maintenance mode turned ${newValue ? 'ON' : 'OFF'}!`, type: 'success' });
      setTimeout(() => setMessage({ text: '', type: '' }), 3000);
    } else {
      // Revert if failed
      setMaintenanceMode(!newValue);
      setMessage({ text: 'Failed to update maintenance mode. Please try again.', type: 'error' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in pl-6">
      <header className="border-b border-slate-800 pb-8">
        <h2 className="text-3xl font-bold tracking-tight text-white mb-2">Dashboard Settings</h2>
        <p className="text-slate-400">
          Manage your website's core settings. Logged in as <span className="text-blue-400">{currentUser?.email}</span>
        </p>
      </header>

      {message.text && (
        <div className={`p-4 rounded-lg text-sm border font-medium ${message.type === 'success' ? 'bg-green-500/10 text-green-400 border-green-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}`}>
          {message.text}
        </div>
      )}

      <div className="grid gap-8">
        {/* Website Title Card */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-white mb-1">Website Title</h3>
              <p className="text-sm text-slate-400">This title displays in the browser tab, navbar, and footer.</p>
            </div>
          </div>
          
          <form onSubmit={handleSaveTitle} className="flex gap-4">
            <input
              type="text"
              className="flex-1 px-4 py-3 rounded-xl bg-slate-950 border border-slate-800 text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 transition-all font-medium"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. ALL BLACK SCREEN"
              required
            />
            <button
              type="submit"
              disabled={saving}
              className={`px-8 py-3 rounded-xl font-medium text-white bg-blue-600 hover:bg-blue-500 transition-colors focus:ring-4 focus:ring-blue-500/20 ${saving ? 'opacity-70 cursor-not-allowed' : ''}`}
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
          </form>
        </div>

        {/* Maintenance Mode Card */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div>
            <h3 className="text-xl font-bold text-white mb-1">Maintenance Mode</h3>
            <p className="text-sm text-slate-400">When ON, visitors will see a maintenance page. Admins can still access the dashboard.</p>
          </div>
          
          <button
            onClick={handleToggleMaintenance}
            className={`relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:ring-offset-2 focus:ring-offset-slate-900 ${maintenanceMode ? 'bg-blue-600' : 'bg-slate-700'}`}
          >
            <span
              className={`inline-block h-7 w-7 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${maintenanceMode ? 'translate-x-6' : 'translate-x-0'}`}
            />
          </button>
        </div>
      </div>
    </div>
  );
}
